import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  MenuItem,
  Stack
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import projectService from '../../services/projectService';
import SaveIcon from '@mui/icons-material/Save';
import FolderIcon from '@mui/icons-material/Folder';
import BaseDialog from './BaseDialog';
import BaseTextField from './BaseTextField';
import BaseButton from './BaseButton';
import { PROJECT_COLORS, getRandomProjectColor } from '../../utils/projectColors';

const ProjectDialog = ({ open, onClose, onSuccess, projectId = null, initialMeetingId = null }) => {
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);
  const [projectData, setProjectData] = useState({
    name: '',
    description: '',
    color: getRandomProjectColor()
  });
  const [validationErrors, setValidationErrors] = useState({});
  const isEditMode = Boolean(projectId);

  useEffect(() => {
    const fetchProjectData = async () => {
      if (!open || !projectId) return;

      setLoadingProject(true);
      try {
        const project = await projectService.getProjectById(projectId);

        if (!project) {
          addSystemNotification('Projet non trouvé', 'error');
          onClose();
          return;
        }

        setProjectData({
          name: project.name || '',
          description: project.description || '',
          color: project.color || getRandomProjectColor()
        });
      } catch (error) {
        addSystemNotification('Erreur lors du chargement du projet', 'error');
        onClose();
      } finally {
        setLoadingProject(false);
      }
    };

    fetchProjectData();
  }, [open, projectId, onClose]);

  const handleClose = () => {
    setProjectData({
      name: '',
      description: '',
      color: getRandomProjectColor()
    });
    setValidationErrors({});
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProjectData({
      ...projectData,
      [name]: value
    });

    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ''
      });
    }
  };

  const validate = () => {
    const errors = {};

    if (!projectData.name.trim()) {
      errors.name = 'Le nom du projet est requis';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);

    try {
      let result;
      const dataToSave = {
        name: projectData.name.trim(),
        description: projectData.description.trim(),
        color: projectData.color
      };

      if (isEditMode) {
        result = await projectService.updateProject(projectId, dataToSave);
        addSystemNotification('Projet mis à jour avec succès', 'success');
      } else {
        result = await projectService.createProject(dataToSave);

        if (initialMeetingId) {
          await projectService.addMeetingToProject(result.id, initialMeetingId);
        }

        addSystemNotification('Projet créé avec succès', 'success');
      }

      if (onSuccess) {
        onSuccess(result);
      }

      handleClose();
    } catch (error) {
      // Détecter si l'erreur est due à un bloqueur de publicités
      const errorMessage = error.message || '';

      if (errorMessage.includes('ERR_BLOCKED_BY_CLIENT') ||
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('NetworkError')) {
        addSystemNotification(
          '🚫 Connexion bloquée : Veuillez désactiver votre bloqueur de publicités (AdBlock/uBlock) pour ce site et réessayer.',
          'error'
        );
      } else if (errorMessage.includes('permission-denied')) {
        addSystemNotification(
          '🔒 Accès refusé : Vous n\'avez pas les permissions nécessaires.',
          'error'
        );
      } else {
        addSystemNotification(
          isEditMode ? 'Erreur lors de la mise à jour du projet' : 'Erreur lors de la création du projet',
          'error'
        );
      }

      console.error('Erreur projet:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (loadingProject) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <BaseTextField
            autoFocus
            required
            fullWidth
            label="Nom du projet"
            name="name"
            value={projectData.name}
            onChange={handleChange}
            error={Boolean(validationErrors.name)}
            helperText={validationErrors.name}
            placeholder="Ex: Refonte application mobile"
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <BaseTextField
            fullWidth
            label="Description"
            name="description"
            value={projectData.description}
            onChange={handleChange}
            multiline
            rows={3}
            placeholder="Décrivez brièvement ce projet..."
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <BaseTextField
            select
            fullWidth
            label="Couleur"
            name="color"
            value={projectData.color}
            onChange={handleChange}
            disabled={loading}
          >
            {PROJECT_COLORS.map((color) => (
              <MenuItem key={color.value} value={color.value}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: color.value,
                      border: '1px solid rgba(0,0,0,0.1)'
                    }}
                  />
                  <Typography>{color.label}</Typography>
                </Stack>
              </MenuItem>
            ))}
          </BaseTextField>
        </Grid>

        {!isEditMode && initialMeetingId && (
          <Grid item xs={12}>
            <Alert severity="info" icon={<FolderIcon />}>
              La réunion actuelle sera automatiquement ajoutée à ce projet.
            </Alert>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title={isEditMode ? 'Modifier le projet' : 'Nouveau projet'}
      maxWidth="sm"
      actions={
        <>
          <BaseButton
            onClick={handleClose}
            color="inherit"
            disabled={loading}
          >
            Annuler
          </BaseButton>
          <BaseButton
            onClick={handleSubmit}
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={loading || loadingProject}
          >
            {isEditMode ? 'Enregistrer' : 'Créer le projet'}
          </BaseButton>
        </>
      }
    >
      {renderContent()}
    </BaseDialog>
  );
};

export default ProjectDialog;
